/**
 * useTaskLookups Hook
 * Centralized hook for fetching and caching all task lookup data
 * (statuses, priorities, progress stages) from the API
 */

import { useQuery } from '@tanstack/react-query';
import {
  useTaskStatusService,
  useTaskPriorityService,
  useTaskProgressStageService,
} from '@actschurch/ccms-sdk';

const LOOKUP_STALE_TIME = 30 * 60 * 1000; // 30 minutes

export function useTaskLookups() {
  const statusService = useTaskStatusService();
  const priorityService = useTaskPriorityService();
  const progressStageService = useTaskProgressStageService();

  const {
    data: statuses = [],
    isLoading: statusesLoading,
  } = useQuery({
    queryKey: ['task-statuses'],
    queryFn: () => statusService.getAll(),
    staleTime: LOOKUP_STALE_TIME,
  });

  const {
    data: priorities = [],
    isLoading: prioritiesLoading,
  } = useQuery({
    queryKey: ['task-priorities'],
    queryFn: () => priorityService.getAll(),
    staleTime: LOOKUP_STALE_TIME,
  });

  const {
    data: progressStages = [],
    isLoading: progressStagesLoading,
  } = useQuery({
    queryKey: ['task-progress-stages'],
    queryFn: () => progressStageService.getAll(),
    staleTime: LOOKUP_STALE_TIME,
  });

  // Lookup maps for quick access by ID
  const statusMap = new Map(statuses.map((s) => [s.statusID, s]));
  const priorityMap = new Map(priorities.map((p) => [p.priorityID, p]));
  const progressStageMap = new Map(progressStages.map((ps) => [ps.progressStageID, ps]));

  // Helper functions
  const getStatusById = (id: number) => statusMap.get(id);
  const getPriorityById = (id: number) => priorityMap.get(id);
  const getProgressStageById = (id: number) => progressStageMap.get(id);

  const getStatusName = (id: number) => statusMap.get(id)?.name ?? 'Unknown';
  const getStatusColor = (id: number) => statusMap.get(id)?.color ?? '#808080';
  const getPriorityName = (id: number) => priorityMap.get(id)?.name ?? 'Unknown';
  const getPriorityColor = (id: number) => priorityMap.get(id)?.color ?? '#808080';
  const getProgressStageName = (id: number) => progressStageMap.get(id)?.name ?? 'Unknown';
  const getProgressStageColor = (id: number) => progressStageMap.get(id)?.color ?? '#808080';

  // Select options for dropdowns - ensure label is always a string for Mantine Select
  const statusOptions = statuses
    .filter((s) => s.name)
    .map((s) => ({
      value: s.statusID.toString(),
      label: String(s.name),
      color: s.color,
    }));

  const priorityOptions = priorities
    .filter((p) => p.name)
    .map((p) => ({
      value: p.priorityID.toString(),
      label: String(p.name),
      color: p.color,
    }));

  const progressStageOptions = progressStages
    .filter((ps) => ps.name)
    .map((ps) => ({
      value: ps.progressStageID.toString(),
      label: String(ps.name),
      color: ps.color,
    }));

  return {
    // Raw data
    statuses,
    priorities,
    progressStages,

    // Loading states
    isLoading: statusesLoading || prioritiesLoading || progressStagesLoading,
    statusesLoading,
    prioritiesLoading,
    progressStagesLoading,

    // Maps
    statusMap,
    priorityMap,
    progressStageMap,

    // Getters
    getStatusById,
    getPriorityById,
    getProgressStageById,
    getStatusName,
    getStatusColor,
    getPriorityName,
    getPriorityColor,
    getProgressStageName,
    getProgressStageColor,

    // Select options
    statusOptions,
    priorityOptions,
    progressStageOptions,
  };
}
